import { MEMBER_PLAN_LABELS, type WorkspaceMembershipSummary } from '@floorpoll/shared';
import { useTourAnchor } from '../lib/tour/anchors.js';
import { track } from '../lib/track.js';
import { useWorkspace } from '../lib/workspace.js';

/**
 * The plan, beside the workspace it belongs to.
 *
 * Plain muted text: no colour, no badge, no dot, no link. `plan-mark`'s fill
 * semantics stay on the account card, and tungsten stays reserved for
 * liveness — a plan is a state, not a broadcast, so it earns no lamp in the
 * chrome either. No date is shown anywhere here, which is also what keeps
 * GRANT_FOREVER off the screen: a granted workspace's proUntil is 9999-12-31,
 * and this component has nowhere to print it.
 *
 * Renders nothing when the plan is unknown. That is not the same as free —
 * `/me` omits it when billing could not be read, and naming a plan we could
 * not read would be a guess.
 */
function PlanLabel({ plan }: { plan: WorkspaceMembershipSummary['plan'] }) {
  if (!plan) return null;
  return (
    <span className="workspace-plan">
      <span aria-hidden="true"> · </span>
      {/* So a screen reader does not run the workspace name and the plan
          together into one phrase. Both spaces in " Plan: " are load-bearing,
          by the same argument: JSX strips the whitespace-only newline on
          each side of this span, so the leading space is the only thing
          separating it from the workspace name (the separator span above is
          aria-hidden, which is exactly what assistive tech skips) and the
          trailing space is the only thing separating it from the plan name
          below. */}
      <span className="visually-hidden"> Plan: </span>
      {MEMBER_PLAN_LABELS[plan]}
    </span>
  );
}

/** `<option>` text cannot carry hidden markup, so the plan reads inline there. */
function optionLabel(workspace: WorkspaceMembershipSummary): string {
  if (!workspace.plan) return workspace.name;
  return `${workspace.name} · ${MEMBER_PLAN_LABELS[workspace.plan]}`;
}

export function WorkspaceSwitcher() {
  const { active, workspaces, setActive } = useWorkspace();
  // Both branches carry the anchor: a new account has exactly one workspace, so
  // the guide would otherwise point at nothing on the very run it exists for.
  const anchor = useTourAnchor('workspace-switcher');

  if (!active) return null;
  // A switcher with one option is just noise.
  if (workspaces.length < 2)
    return (
      <span className="workspace-name" ref={anchor}>
        {active.name}
        <PlanLabel plan={active.plan} />
      </span>
    );

  return (
    <label className="workspace-switcher" ref={anchor}>
      <span className="visually-hidden">Active workspace</span>
      <select
        value={active.workspaceId}
        onChange={(e) => {
          setActive(e.target.value);
          track('workspaceSwitched');
        }}
      >
        {workspaces.map((workspace) => (
          <option key={workspace.workspaceId} value={workspace.workspaceId}>
            {optionLabel(workspace)}
          </option>
        ))}
      </select>
    </label>
  );
}
